/* ==========================================================================
   fk-search.js — type-ahead for the two FK search surfaces.

   WHY NOT DAWN'S <predictive-search>. Predictive search is enabled in the
   theme settings and Dawn's predictive-search.js does load, but it only ever
   runs on /search: the FK header search is a hand-rolled GET form with no
   <predictive-search> wrapper, no combobox ARIA and no results container, so
   nothing in Dawn could bind to it. Adopting Dawn's element would mean
   satisfying its full DOM contract inside a header that shares none of its
   markup — and Dawn's own header (sections/header.liquid) is disabled in this
   theme, so there is no second consumer to keep in step with. This talks to
   Shopify's /search/suggest.json directly instead.

   ONE MODULE, TWO SURFACES. The desktop takeover and the mobile drawer each
   carry their own [data-fk-search-panel]; every function takes the surface's
   root so the two never share state. Listeners are delegated from document so
   a section re-render in the theme editor needs no rebinding.

   CAUTION — assets/main-search.js:36 (`keepInSync`) writes the value of any
   search input into EVERY other search input on the page, including both of
   ours. That is why the fetch is keyed off the event target's own value and
   the panel is closed whenever the query is shorter than MIN.
   ========================================================================== */
(function () {
  'use strict';

  var MIN = 2;          /* one letter matches most of the catalogue */
  var DEBOUNCE = 220;   /* long enough to skip mid-word keystrokes */
  var LIMIT = 4;

  var timers = {};      /* per surface, so the two do not cancel each other */
  var controllers = {}; /* in-flight fetch per surface, aborted on the next keystroke */

  function surfaceOf(el) { return el ? el.closest('[data-fk-search-takeover]') : null; }
  function surfaceKey(root) { return root ? root.getAttribute('data-fk-search-takeover') : ''; }
  function panelOf(root) { return root ? root.querySelector('[data-fk-search-panel]') : null; }

  function money(cents) {
    /* /search/suggest.json returns price as a decimal STRING ("35.00"), not the
       cents integer the Cart API uses — passing it through a cents formatter
       would render $3,500. */
    var n = parseFloat(cents);
    if (isNaN(n)) return '';
    return '$' + (n % 1 === 0 ? n.toFixed(0) : n.toFixed(2));
  }

  /* `styled_text` is Shopify-generated HTML carrying <mark> around the matched
     span. It is built from the shopper's own query, so it is never inserted
     raw — only <mark> and <span> survive, everything else becomes text. */
  function safeStyled(html) {
    var tmp = document.createElement('div');
    tmp.innerHTML = String(html || '');
    var out = document.createElement('span');
    Array.prototype.forEach.call(tmp.childNodes, function walk(node) {
      if (node.nodeType === 3) {
        out.appendChild(document.createTextNode(node.nodeValue));
      } else if (node.nodeType === 1 && (node.tagName === 'MARK' || node.tagName === 'SPAN')) {
        var el = document.createElement(node.tagName.toLowerCase());
        el.textContent = node.textContent;
        out.appendChild(el);
      } else if (node.nodeType === 1) {
        out.appendChild(document.createTextNode(node.textContent));
      }
    });
    return out;
  }

  function clearPanel(root) {
    var panel = panelOf(root);
    if (!panel) return;
    panel.hidden = true;
    panel.innerHTML = '';
    var input = root.querySelector('[data-fk-search-input]');
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
  }

  function rowsIn(panel) {
    return Array.prototype.slice.call(panel.querySelectorAll('[data-fk-search-row]'));
  }

  function highlight(panel, index) {
    var rows = rowsIn(panel);
    rows.forEach(function (r, i) {
      var on = i === index;
      r.classList.toggle('is-active', on);
      if (on) {
        r.setAttribute('aria-selected', 'true');
        if (r.scrollIntoView) r.scrollIntoView({ block: 'nearest' });
      } else {
        r.setAttribute('aria-selected', 'false');
      }
    });
    var input = panel.closest('[data-fk-search-takeover]').querySelector('[data-fk-search-input]');
    if (input) {
      if (index >= 0 && rows[index]) input.setAttribute('aria-activedescendant', rows[index].id);
      else input.removeAttribute('aria-activedescendant');
    }
  }

  function activeIndex(panel) {
    return rowsIn(panel).findIndex(function (r) { return r.classList.contains('is-active'); });
  }

  function render(root, data, term) {
    var panel = panelOf(root);
    if (!panel) return;
    var key = surfaceKey(root);
    var res = (data && data.resources && data.resources.results) || {};
    var products = res.products || [];
    var collections = res.collections || [];
    var queries = res.queries || [];

    panel.innerHTML = '';
    var rowId = 0;

    if (!products.length && !collections.length && !queries.length) {
      var empty = document.createElement('p');
      empty.className = 'fk-sug__empty';
      empty.textContent = 'Nothing matched “' + term + '”. Try a colour, a size, or an occasion.';
      panel.appendChild(empty);
      panel.hidden = false;
      return;
    }

    function group(label) {
      var h = document.createElement('p');
      h.className = 'fk-sug__label';
      h.textContent = label;
      panel.appendChild(h);
    }

    function list() {
      var ul = document.createElement('ul');
      ul.className = 'fk-sug__list';
      ul.setAttribute('role', 'presentation');
      panel.appendChild(ul);
      return ul;
    }

    if (products.length) {
      group('Products');
      var pl = list();
      products.forEach(function (p) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'fk-sug__row fk-sug__row--product';
        a.href = p.url;
        a.id = 'fk-sug-' + key + '-' + (rowId++);
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.setAttribute('data-fk-search-row', '');

        var img = p.featured_image && p.featured_image.url;
        if (img) {
          var im = document.createElement('img');
          im.className = 'fk-sug__thumb';
          im.src = img.replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
          im.alt = '';
          im.loading = 'lazy';
          im.width = 44; im.height = 44;
          a.appendChild(im);
        } else {
          var ph = document.createElement('span');
          ph.className = 'fk-sug__thumb fk-sug__thumb--empty';
          a.appendChild(ph);
        }

        var meta = document.createElement('span');
        meta.className = 'fk-sug__meta';
        var t = document.createElement('span');
        t.className = 'fk-sug__title';
        t.textContent = p.title;
        meta.appendChild(t);
        var pr = document.createElement('span');
        pr.className = 'fk-sug__price';
        pr.textContent = p.available === false ? 'Sold out' : money(p.price);
        meta.appendChild(pr);
        a.appendChild(meta);

        li.appendChild(a);
        pl.appendChild(li);
      });
    }

    if (collections.length) {
      group('Collections');
      var cl = list();
      collections.forEach(function (c) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'fk-sug__row';
        a.href = c.url;
        a.id = 'fk-sug-' + key + '-' + (rowId++);
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.setAttribute('data-fk-search-row', '');
        a.textContent = c.title;
        li.appendChild(a);
        cl.appendChild(li);
      });
    }

    if (queries.length) {
      group('Suggestions');
      var ql = list();
      queries.forEach(function (q) {
        var li = document.createElement('li');
        var a = document.createElement('a');
        a.className = 'fk-sug__row fk-sug__row--query';
        a.href = q.url;
        a.id = 'fk-sug-' + key + '-' + (rowId++);
        a.setAttribute('role', 'option');
        a.setAttribute('aria-selected', 'false');
        a.setAttribute('data-fk-search-row', '');
        a.appendChild(safeStyled(q.styled_text || q.text || ''));
        li.appendChild(a);
        ql.appendChild(li);
      });
    }

    /* Always a way through to the full results page — the panel is a shortcut,
       never the only route. */
    var all = document.createElement('a');
    all.className = 'fk-sug__all';
    all.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root : '/') + 'search?q=' + encodeURIComponent(term);
    all.id = 'fk-sug-' + key + '-' + (rowId++);
    all.setAttribute('role', 'option');
    all.setAttribute('aria-selected', 'false');
    all.setAttribute('data-fk-search-row', '');
    all.textContent = 'See all results for “' + term + '”';
    panel.appendChild(all);

    panel.hidden = false;
    var input = root.querySelector('[data-fk-search-input]');
    if (input) input.setAttribute('aria-expanded', 'true');
  }

  function query(root, term) {
    var key = surfaceKey(root);
    if (controllers[key]) { try { controllers[key].abort(); } catch (e) { /* already settled */ } }
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    controllers[key] = ctrl;

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.predictive_search_url) || '/search/suggest';
    var url = base + '.json?q=' + encodeURIComponent(term)
      + '&resources[type]=product,collection,query'
      + '&resources[limit]=' + LIMIT
      + '&resources[options][unavailable_products]=last';

    fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) { if (d) render(root, d, term); })
      .catch(function () { /* aborted, offline, or a 5xx — leave the last panel alone */ });
  }

  document.addEventListener('input', function (e) {
    var input = e.target.closest('[data-fk-search-input]');
    if (!input) return;
    var root = surfaceOf(input);
    if (!root) return;
    var key = surfaceKey(root);
    var term = input.value.trim();

    clearTimeout(timers[key]);
    if (term.length < MIN) { clearPanel(root); return; }
    timers[key] = setTimeout(function () { query(root, term); }, DEBOUNCE);
  });

  document.addEventListener('keydown', function (e) {
    var input = e.target.closest('[data-fk-search-input]');
    if (!input) return;
    var root = surfaceOf(input);
    var panel = panelOf(root);
    if (!panel || panel.hidden) return;

    var rows = rowsIn(panel);
    if (!rows.length) return;
    var i = activeIndex(panel);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      highlight(panel, i + 1 >= rows.length ? 0 : i + 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      highlight(panel, i - 1 < 0 ? rows.length - 1 : i - 1);
    } else if (e.key === 'Enter') {
      /* Only hijack Enter when a row is actually highlighted; otherwise the
         form submits to /search as it always did. */
      if (i >= 0 && rows[i]) { e.preventDefault(); window.location.href = rows[i].href; }
    } else if (e.key === 'Escape') {
      /* Consumed here so the shared Escape chain in fk-ui.js does not also
         close the whole search or the nav drawer in the same keystroke. */
      e.stopPropagation();
      clearPanel(root);
    }
  });

  /* A click anywhere outside a surface closes that surface's panel. */
  document.addEventListener('click', function (e) {
    document.querySelectorAll('[data-fk-search-takeover]').forEach(function (root) {
      if (!root.contains(e.target)) clearPanel(root);
    });
  });

  /* Closing the search UI must not leave a panel behind for the next open. */
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-fk-search-close]')) {
      document.querySelectorAll('[data-fk-search-takeover]').forEach(clearPanel);
    }
  });
})();
